/**
 * Pure snapshot → step classification. No React, no I/O — this is the heart of
 * "render from JSON": given the backend's collection-flow snapshot, decide what
 * the applicant sees, using the state's tags as the intent signal.
 *
 * Why tags and not `available_events` alone: the engine reports every event a
 * state can fire, including operator actions (e.g. `APPROVE` in `manual_review`).
 * The applicant must not see those. Tags — stamped by the workflow builder's
 * catalog — tell us the step's intent (collect input / wait for review / done).
 */
import type { CollectionFlowSnapshot, StepView } from './types';

const APPROVED = 'approved';
const REJECTED = 'rejected';
const MANUAL_REVIEW = 'manual_review';

/**
 * A state that declares its control unimplemented and halts for a human.
 *
 * Without this the applicant sees the generic "Under review" wait screen, which
 * is a lie of omission: nothing is being reviewed, the case is parked until an
 * operator records an attestation. Same pixels, wrong story — and the applicant
 * has no way to tell a 30-second automated check from an indefinite wait.
 */
const BLOCKED_OPERATOR = 'blocked_operator';

// Events an applicant can fire themselves — mirrors the branches `FlowRunner`
// renders and the `flow.event.*` labels. Every OTHER event a state exposes
// (e.g. `REVIEW_CONFIRMED`, `APPROVE`, `REJECT`) is operator/system-only and must
// never surface to the applicant as a button. Keep in sync with FlowRunner's
// per-event handlers.
const APPLICANT_EVENTS = new Set([
  'CONSENT_GIVEN',
  'ID_FRONT_CAPTURED',
  'ID_BACK_CAPTURED',
  // The other documents a step may accept (./documents) — the applicant's
  // choice of document is which of these they fire.
  'DRIVING_LICENSE_FRONT_CAPTURED',
  'DRIVING_LICENSE_BACK_CAPTURED',
  'PASSPORT_CAPTURED',
  // The business registration certificate (KYB, ./businessDocument). A partner
  // or an operator may supply it instead; their upload fires the same event.
  'BUSINESS_DOC_CAPTURED',
  'LIVENESS_DONE',
  'DETAILS_SUBMITTED',
  // Proving control of the collected number. The applicant's own act: they
  // receive the SMS and type the code. The server re-checks its evidence when
  // the event lands, so listing it here widens the UI, not the trust boundary.
  'PHONE_VERIFIED',
  // The e-signature ceremony. Both are the applicant's own act: signing is the
  // legal act itself, and declining is the applicant's right to refuse it — the
  // seed routes SIGNATURE_DECLINED to manual review rather than to a rejection.
  'SIGNATURE_SUBMITTED',
  'SIGNATURE_DECLINED',
  // The NFC document chip (./documentChip). READ is fired by the SERVER once it
  // has verified what the native reader relayed — the events route refuses it
  // from a client — and is listed so the step renders. UNAVAILABLE ("this
  // device cannot read the chip") is the applicant's own.
  'DOCUMENT_CHIP_READ',
  'DOCUMENT_CHIP_UNAVAILABLE',
  // The dip chip (./dipChip): a Thai ID card in a contact reader. Fired by the
  // SERVER when a partner terminal submits its sealed read; listed so the step
  // renders instead of reading as "Under review".
  'CHIP_READ',
  // The Company Registry step (./company): the server re-fetches the registry
  // record on this event, so its payload is the applicant's picks only.
  'COMPANY_SUBMITTED',
  // Key people (./keyPeople). DONE is fired by the SERVER once every person's
  // KYC has an outcome — the events route refuses it from a client — and is
  // listed so the awaiting screen renders.
  'KEY_PEOPLE_SUBMITTED',
  'KEY_PEOPLE_DONE',
  // Grouped documents (./DocumentGroupsStep).
  'DOCUMENTS_SUBMITTED',
  // A questionnaire (./questionnaire): the server re-walks its branches and
  // judges every answer, so this widens the UI, not the trust boundary.
  'QUESTIONNAIRE_SUBMITTED',
  'SUBMIT',
]);

/** Classify a snapshot into the renderable step the applicant should see. */
export function stepFor(snapshot: CollectionFlowSnapshot): StepView {
  const { state, status, tags, done } = snapshot;

  const isTerminal =
    done ||
    status === 'completed' ||
    status === 'failed' ||
    tags.includes(APPROVED) ||
    tags.includes(REJECTED);

  if (isTerminal) {
    return { kind: 'terminal', state, status, tags, actions: [], outcome: outcomeOf(snapshot) };
  }

  // Checked BEFORE manual_review: a state can carry both, and the blocked copy
  // is the more specific truth.
  if (tags.includes(BLOCKED_OPERATOR)) {
    return { kind: 'blocked', state, status, tags, actions: [] };
  }

  if (tags.includes(MANUAL_REVIEW)) {
    // The applicant waits; the decision events belong to the operator.
    return { kind: 'review', state, status, tags, actions: [] };
  }

  // Only applicant-fireable events become actions — a state may also expose
  // operator/system events (e.g. `awaiting_review` exposes REVIEW_CONFIRMED),
  // which the applicant must never see or be able to trigger.
  const actions = snapshot.available_events.filter((e) => APPLICANT_EVENTS.has(e));

  // No applicant action left → the applicant has handed off and is waiting for an
  // operator/system move. Show the "Under review" wait screen — not a raw
  // operator button, nor an untranslated state title.
  if (actions.length === 0) {
    return { kind: 'review', state, status, tags, actions: [] };
  }

  return { kind: 'collect', state, status, tags, actions };
}

/** Resolve the terminal outcome for messaging (tags win over raw status). */
function outcomeOf(snapshot: CollectionFlowSnapshot): StepView['outcome'] {
  if (snapshot.tags.includes(APPROVED)) return 'approved';
  if (snapshot.tags.includes(REJECTED)) return 'rejected';
  return snapshot.status === 'failed' ? 'failed' : 'completed';
}
